import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Box, Stack, MenuItem,
  Autocomplete, CircularProgress, Alert,
} from '@mui/material';
import { Add as AddIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import OtableFileStart from '../publication/OtableFileStart';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'pl', label: 'Polish' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
];

const CreateProposalDialog = ({
  createProposalDialogOpen,
  setCreateProposalDialogOpen,
  proposalForm,
  setProposalForm,
  handleCreateProposal,
  consortium,
  existingProposals = [],
  handleCreateFromFile,          // (file) => Promise: a proposal from an Otable document (.otable)
}) => {
  const [selectedCall, setSelectedCall] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState('');
  const [calls, setCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callSearchQuery, setCallSearchQuery] = useState('');
  const [duplicateError, setDuplicateError] = useState(null);

  useEffect(() => {
    if (!createProposalDialogOpen) {
      setSelectedCall(null);
      setCallSearchQuery('');
      setDuplicateError(null);
    }
  }, [createProposalDialogOpen]);

  useEffect(() => {
    if (!createProposalDialogOpen) return;
    setLoadingCalls(true);
    axiosService.get('/funding/calls/', { params: { page_size: 30 } })
      .then(res => setCalls(res?.data?.results || res?.data || []))
      .catch(() => setCalls([]))
      .finally(() => setLoadingCalls(false));
  }, [createProposalDialogOpen]);

  useEffect(() => {
    if (callSearchQuery.length < 2) {
      if (callSearchQuery === '') {
        setLoadingCalls(true);
        axiosService.get('/funding/calls/', { params: { page_size: 30 } })
          .then(res => setCalls(res?.data?.results || res?.data || []))
          .catch(() => setCalls([]))
          .finally(() => setLoadingCalls(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingCalls(true);
      axiosService.get('/funding/calls/', { params: { search: callSearchQuery, page_size: 20 } })
        .then(res => setCalls(res?.data?.results || res?.data || []))
        .catch(() => setCalls([]))
        .finally(() => setLoadingCalls(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [callSearchQuery]);

  const handleCallChange = (_, newCall) => {
    setSelectedCall(newCall);
    setDuplicateError(null);

    if (newCall) {
      const duplicate = existingProposals.find(p => p.call && String(p.call) === String(newCall.id));
      if (duplicate) {
        setDuplicateError(
          `This consortium already has a proposal ("${duplicate.acronym}") for this funding call.`
        );
      }
    }

    setProposalForm(prev => ({ ...prev, call: newCall?.id || null }));
  };

  const handleClose = () => setCreateProposalDialogOpen(false);

  const set = (field) => (e) => setProposalForm(prev => ({ ...prev, [field]: e.target.value }));

  const canSubmit = proposalForm.acronym?.trim() && !duplicateError;

  return (
    <Dialog open={createProposalDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon />
          Create New Proposal
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a new proposal for "{consortium?.name}". You can fill in the detailed contents later.
        </Typography>

        {duplicateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDuplicateError(null)}>
            {duplicateError}
          </Alert>
        )}

        <Stack spacing={2}>
          {/* Row 1: Acronym + Duration */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Acronym"
              fullWidth
              value={proposalForm.acronym}
              onChange={set('acronym')}
              placeholder={`e.g., ${consortium?.name}-Project1`}
              required
            />
            <TextField
              label="Duration (months)"
              type="number"
              fullWidth
              value={proposalForm.duration_months}
              onChange={set('duration_months')}
              placeholder={consortium?.duration_months?.toString() || '24'}
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Stack>

          {/* Row 2: Title */}
          <TextField
            label="Title"
            fullWidth
            value={proposalForm.title}
            onChange={set('title')}
            placeholder="Enter proposal title (optional — can be set later)"
          />

          {/* Row 3: Funding Call */}
          <Autocomplete
            value={selectedCall}
            onChange={handleCallChange}
            onInputChange={(_, newInput, reason) => {
              if (reason === 'input') setCallSearchQuery(newInput);
              else if (reason === 'clear') setCallSearchQuery('');
            }}
            options={calls}
            getOptionLabel={(option) => option?.title || option?.code || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(x) => x}
            loading={loadingCalls}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Related Funding Call"
                placeholder="Search for a funding call… (optional)"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <CampaignIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingCalls && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
                error={!!duplicateError}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{option.code || 'No code'}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.title}</Typography>
                </Box>
              </li>
            )}
          />

          {/* Row 4: Language + Ethics */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="AI Output Language"
              select
              fullWidth
              value={proposalForm.language || 'en'}
              onChange={set('language')}
              helperText="The AI writing assistant will produce text in this language"
            >
              {LANGUAGES.map(l => (
                <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Ethics Review Required"
              select
              fullWidth
              value={proposalForm.ethics_flag ?? false}
              onChange={(e) => setProposalForm(prev => ({
                ...prev,
                ethics_flag: e.target.value === 'true',
              }))}
            >
              <MenuItem value={false}>No</MenuItem>
              <MenuItem value={true}>Yes</MenuItem>
            </TextField>
          </Stack>
        </Stack>
        {handleCreateFromFile && (
          <OtableFileStart kind="proposal" busy={fileBusy} error={fileError} onFile={async (file) => {
            setFileBusy(true); setFileError('');
            try { await handleCreateFromFile(file); } catch (err) { setFileError(err.message); } finally { setFileBusy(false); }
          }} />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreateProposal}
          variant="contained"
          disabled={!canSubmit}
        >
          Create Proposal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProposalDialog;
